// Комментарий на русском:
// Единый детектор окружения клиента (браузер/ОС/устройство).
// Зачем:
// - подбирать форматы медиа (аудио/изображения) и размеры/качество под разные платформы
// - логировать ошибки с понятным "Windows/Chrome", "iOS/Safari", и т.п.

export type ClientOS = 'windows' | 'macos' | 'linux' | 'ios' | 'android' | 'unknown';
export type ClientBrowser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'opera' | 'unknown';
export type ClientDevice = 'mobile' | 'tablet' | 'desktop' | 'unknown';

export interface ClientEnv {
  os: ClientOS;
  browser: ClientBrowser;
  device: ClientDevice;
  isStandalone: boolean;
  isTouch: boolean;
  ua: string;
}

let _cached: ClientEnv | null = null;

function safeUA(): string {
  try {
    return typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '';
  } catch {
    return '';
  }
}

export function detectOS(uaRaw?: string): ClientOS {
  const ua = String(uaRaw ?? safeUA()).toLowerCase();
  if (!ua) return 'unknown';
  if (ua.includes('android')) return 'android';
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios';
  if (ua.includes('windows nt')) return 'windows';
  // iPadOS иногда маскируется под Mac.
  if (ua.includes('macintosh') && ua.includes('mobile')) return 'ios';
  if (ua.includes('mac os x') || ua.includes('macintosh')) return 'macos';
  if (ua.includes('linux')) return 'linux';
  return 'unknown';
}

export function detectBrowser(uaRaw?: string): ClientBrowser {
  const ua = String(uaRaw ?? safeUA()).toLowerCase();
  if (!ua) return 'unknown';
  // порядок важен: Edge/Opera содержат "chrome"
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('opr/') || ua.includes('opera')) return 'opera';
  if (ua.includes('firefox/')) return 'firefox';
  // Safari: есть "safari", но нет "chrome"/"chromium"/"crios"
  const isSafari =
    ua.includes('safari') &&
    !ua.includes('chrome') &&
    !ua.includes('chromium') &&
    !ua.includes('crios') &&
    !ua.includes('edg/') &&
    !ua.includes('opr/');
  if (isSafari) return 'safari';
  if (ua.includes('chrome') || ua.includes('crios')) return 'chrome';
  return 'unknown';
}

export function detectDevice(uaRaw?: string): ClientDevice {
  const ua = String(uaRaw ?? safeUA()).toLowerCase();
  if (!ua) return 'unknown';
  // iPadOS/Tablet
  if (ua.includes('ipad') || ua.includes('tablet')) return 'tablet';
  // Android: tablet часто без "mobile"
  if (ua.includes('android') && !ua.includes('mobile')) return 'tablet';
  if (ua.includes('mobi') || ua.includes('iphone') || ua.includes('ipod')) return 'mobile';
  return 'desktop';
}

export function isStandaloneApp(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    // iOS Safari
    if (typeof (navigator as any)?.standalone === 'boolean' && (navigator as any).standalone) return true;
    // PWA display-mode
    if (typeof window.matchMedia === 'function') {
      return !!window.matchMedia('(display-mode: standalone)').matches;
    }
  } catch {}
  return false;
}

export function getClientEnv(): ClientEnv {
  if (_cached) return _cached;
  const ua = safeUA();
  const os = detectOS(ua);
  const browser = detectBrowser(ua);
  const device = detectDevice(ua);
  const isStandalone = isStandaloneApp();
  const isTouch = (() => {
    try {
      if (typeof window === 'undefined') return false;
      return 'ontouchstart' in window || (navigator as any)?.maxTouchPoints > 0;
    } catch {
      return false;
    }
  })();
  _cached = { os, browser, device, isStandalone, isTouch, ua };
  return _cached;
}

export function isSafariLike(env?: ClientEnv): boolean {
  const e = env || getClientEnv();
  return e.browser === 'safari' || e.os === 'ios';
}


